import { useCallback } from 'react'

import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import type { DialogProps } from '@/dialogs/types'
import { useListMutations } from '@/modules/tiktok-research/hooks/useListMutations.ts'
import { CreateListForm } from '@/modules/tiktok-research/videos/ui/create-list-form/CreateListForm.tsx'
import type { CreateListFormValues } from '@/modules/tiktok-research/videos/ui/create-list-form/validations.ts'
import type { TikTokContentListType } from '@/modules/tiktok-research/videos/ui/types'

interface Props extends DialogProps {
  list?: TikTokContentListType
}

const FORM_ID = 'list-form'

export const CreateOrEditTikTokListDialog = ({
  isOpen,
  onClose,
  list,
}: Props) => {
  const { createList, editList } = useListMutations()

  const handleSubmit = useCallback(
    async (values: CreateListFormValues) => {
      if (list) {
        await editList.mutateAsync({ ...list, ...values })
      } else {
        await createList.mutateAsync(values)
      }
      onClose()
    },
    [list, onClose, editList, createList],
  )

  const title = list ? 'Edit List' : 'Create List'

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
      bigDialog={true}
      panelClassName="overflow-visible"
    >
      <Dialog.Header className="mb-6">
        <Dialog.Title>{title}</Dialog.Title>
      </Dialog.Header>
      <Dialog.Description>
        <CreateListForm
          onSubmit={handleSubmit}
          formId={FORM_ID}
          defaultValues={list}
        />
      </Dialog.Description>
      <Dialog.ClosableFooter onClose={onClose}>
        <Button
          variant="default"
          type="submit"
          form={FORM_ID}
          isLoading={createList.isLoading || editList.isLoading}
        >
          Save
        </Button>
      </Dialog.ClosableFooter>
    </Dialog>
  )
}
